import {useContext, useEffect, useMemo} from 'react';
import {
  ImageBackground,
  Platform,
  StatusBar,
  StyleSheet,
  View,
} from 'react-native';
import {withForwardedNavigationParams} from 'react-navigation-props-mapper';
import withIsConnected from '../../hoc/withIsConnected';
import Localized from 'src/constants/AppStrings';
import Styles from 'src/components/Styles';
import {IsConnectedProps} from 'src/types/Screen';
import FirebaseAnalytic from 'src/nativeModules/FirebaseAnalytic';
import {useAppSelector} from 'src/redux/hooks';
import MainConsumerContext from 'src/components/MainConsumerContext';
import AccountStore from 'src/stores/AccountStore';
import QRCode from 'react-native-qrcode-svg';
import AVText from 'src/components/elements/AVText';
import AVFormattedCurrency from 'src/components/elements/AVFormattedCurrency';
import RoundedButton, {ButtonType} from 'src/components/elements/RoundedButton';
import Util, {capitalizeFirstWord, getTotalDisplayAmount} from 'src/Util';
import Events from 'src/logging/Events';
import {alertError} from 'src/components/helpers/AlertHelper';
import PassKit from 'src/nativeModules/PassKit';
import LinearGradient from 'react-native-linear-gradient';
import {getDescriber} from 'src/components/elements/descriptor/DescriptorType';
import {getLayoutDescriber} from 'src/components/elements/layoutDescriptor/DescriptorType';
import BrightnessTouchable from 'src/components/elements/BrightnessTouchable';
import {BalanceTypes} from '../../../types/serverTypes/Account';
import Settings from 'src/Settings';
import Header from 'src/components/elements/Header';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useBottomTabBarHeight} from '@react-navigation/bottom-tabs';
import {getButtonDescriber} from 'src/components/elements/buttonDescriptor/DescriptorType';

type ScanAccountScreenProps = IsConnectedProps;

const ScanAccountScreen = (props: ScanAccountScreenProps) => {
  const context = useContext(MainConsumerContext);
  const insets = useSafeAreaInsets();
  const tabBarHeight = useBottomTabBarHeight();

  const overlayHeight =
    Styles.ScreenHeight -
    (tabBarHeight + Styles.Heights.touchTargetHeight2 + insets.top);

  const scaledHeight = overlayHeight * 0.95;

  const displayBalances = useAppSelector(
    (state) => state.account.account.displayBalances,
  );
  const defaultBalance = displayBalances.find(
    (b) => b.type === BalanceTypes.Default,
  );
  useEffect(() => {
    FirebaseAnalytic.trackEvent('componentDidMount', 'ScanAccountScreen', {
      ...props,
    });
  }, []);

  const addPassToWallet = async () => {
    const {state, actions} = context;
    FirebaseAnalytic.trackEvent('addPassToWallet', 'ScanAccountScreen');
    actions.showSpinner();
    try {
      const addPasssuccess = await PassKit.addPass(state.passUrl);
      if (addPasssuccess) {
        const isPassExists = await PassKit.doesPassExist(state.passUrl);

        if (isPassExists) {
          if (actions.passAdded) {
            actions.passAdded();
          }
          Events.Pass.trackEvent();
        }
      } else {
        alertError(Localized.Errors.failed_to_add_pass);
      }
    } finally {
      actions.hideSpinner();
    }
  };

  let timeoutId;
  const addFundsAction = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    FirebaseAnalytic.trackEvent('addFundClicked', 'ScanAccountScreen');

    if (Platform.OS === 'android') {
      timeoutId = setTimeout(() => {
        context.actions.navigateToFunding(props.isConnected, {
          transDate: Util.getCurrentDate(),
        });
      }, 300);
    } else {
      context.actions.navigateToFunding(props.isConnected, {
        transDate: Util.getCurrentDate(),
      });
    }
  };

  const scanScreenDescriptionButtons = () => {
    const buttonColor = getButtonDescriber().checkRoundedBtnColor();
    return (
      <View style={styles.descMain}>
        <View style={styles.balanceContainer}>
          <View style={styles.balanceView}>
            <AVText
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
              style={[
                getDescriber().scanScreenQRCodeStyleDescriptor().balanceText,
              ]}
              accessibilityRole="text"
              accessible={true}
              accessibilityLabel={Localized.Labels.my_balance}
              aria-label={`${Localized.Labels.my_balance}, text`}
            >
              {Localized.Labels.my_balance + ': '}
            </AVText>
            <AVFormattedCurrency
              testID={'balanceFormattedCurrencyScanAccount'}
              accessibilityLabel={`${
                Localized.Labels.my_balance
              } ${Util.formatCurrency(
                null,
                getTotalDisplayAmount(displayBalances),
                defaultBalance?.currency ?? AccountStore.getCurrency(),
              )}`}
              aria-label={`${Localized.Labels.my_balance} ${Util.formatCurrency(
                null,
                getTotalDisplayAmount(displayBalances),
                defaultBalance?.currency ?? AccountStore.getCurrency(),
              )}`}
              maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm0}
              style={[
                getDescriber().scanScreenQRCodeStyleDescriptor().balanceText,
              ]}
              value={getTotalDisplayAmount(displayBalances)}
              currency={defaultBalance?.currency ?? AccountStore.getCurrency()}
            />
          </View>
        </View>
        <View style={styles.btnMainView}>
          <View
            style={
              getDescriber().scanScreenQRCodeStyleDescriptor()
                .addFundsButtonContainer
            }
          >
            <RoundedButton
              accessible={true}
              accessibilityHint="Double tap to open funding modal"
              accessibilityRole="button"
              role="button"
              accessibilityLabel={Localized.Labels.add_funds}
              aria-label={Localized.Labels.add_funds}
              testID={'qa_btn_Fund'}
              buttonType={ButtonType.normal}
              text={
                Settings.isRefive()
                  ? capitalizeFirstWord(Localized.Labels.add_funds)
                  : Localized.Labels.add_funds
              }
              textStyle={[
                getDescriber().scanScreenQRCodeStyleDescriptor().addFundsText,
                {color: Styles.white},
              ]}
              color={buttonColor}
              onPress={() => addFundsAction()}
              containerStyle={
                getDescriber().scanScreenQRCodeStyleDescriptor().btnAddFund
              }
            />
          </View>
          <View style={styles.bottomButtonsView}>
            {!context?.state.passExists && context?.state.canAddPass && (
              <RoundedButton
                accessible={true}
                accessibilityHint="Double tap to add to wallet"
                accessibilityRole="button"
                role="button"
                accessibilityLabel={Localized.Buttons.add_to_wallet}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm4}
                aria-label={Localized.Buttons.add_to_wallet}
                testID={Localized.Buttons.add_to_wallet}
                buttonType={ButtonType.normal}
                text={
                  Settings.isRefive()
                    ? capitalizeFirstWord(Localized.Buttons.add_to_wallet)
                    : Localized.Buttons.add_to_wallet
                }
                textStyle={[
                  getDescriber().scanScreenQRCodeStyleDescriptor()
                    .addToWalletText,
                ]}
                containerStyle={
                  getDescriber().scanScreenQRCodeStyleDescriptor()
                    .btnAddToWallet
                }
                color={buttonColor}
                backgroundColor={Styles.white}
                onPress={() => addPassToWallet()}
              />
            )}
          </View>
        </View>
      </View>
    );
  };

  const content = useMemo(() => {
    if (Settings.isRefive()) {
      return (
        <Header title={getDescriber().scanScreenQRCodeHeaderText()}>
          <View style={styles.container}>
            <ImageBackground
              source={require('src/components/img/welcomedotoverlay.png')}
              style={[styles.overlay, {height: scaledHeight}]}
              resizeMode="cover"
            >
              <View style={styles.qrView}>
                <ImageBackground
                  source={require('src/components/img/scanrectangle.png')}
                  style={styles.refiveBackground}
                >
                  <BrightnessTouchable
                    style={styles.qrCodeView}
                    accessibilityLabel="QRCode"
                    aria-label="QRCode"
                    testID="QRCode"
                    timeout={10000}
                  >
                    <QRCode
                      value={AccountStore.getQrCode() ?? ''}
                      size={Styles.ScreenWidth / 2.2}
                    />
                  </BrightnessTouchable>
                </ImageBackground>
                {scanScreenDescriptionButtons()}
              </View>
            </ImageBackground>
          </View>
        </Header>
      );
    } else if (Settings.isAramark()) {
      return (
        <View style={[styles.redesignContainer, styles.aramarkContainer]}>
          <StatusBar translucent={true} backgroundColor="transparent" />
          <View style={styles.scanHeaderTextView}>
            <AVText style={styles.scanHeaderTextDesc}>
              {getDescriber().scanScreenQRCodeHeaderText()}
            </AVText>
          </View>

          <View style={styles.gradientBoxView}>
            <View style={[styles.gradientBox, styles.aramarkQRCodeView]}>
              <BrightnessTouchable
                style={styles.redesignQrCodeView}
                accessibilityLabel="QRCode"
                aria-label="QRCode"
                testID="QRCode"
                timeout={10000}
              >
                <QRCode
                  value={AccountStore.getQrCode() ?? ''}
                  size={Styles.ScreenWidth / 2.2}
                />
              </BrightnessTouchable>
            </View>
          </View>

          {scanScreenDescriptionButtons()}
        </View>
      );
    } else {
      return (
        <LinearGradient
          colors={['#FEE0E6', '#E6ECFF']}
          start={{x: 0, y: 0}}
          end={{x: 0, y: 1}}
          style={styles.redesignContainer}
        >
          <StatusBar translucent={true} backgroundColor="transparent" />
          <View style={styles.scanHeaderTextView}>
            <AVText style={styles.scanHeaderTextDesc}>
              {getDescriber().scanScreenQRCodeHeaderText()}
            </AVText>
          </View>

          <View style={styles.gradientBoxView}>
            <LinearGradient
              style={styles.gradientBox}
              start={{x: 0, y: 0}}
              end={{x: 1, y: 1}}
              colors={['#FEE0E6', '#E6ECFF']}
            >
              <BrightnessTouchable
                style={styles.redesignQrCodeView}
                accessibilityLabel="QRCode"
                aria-label="QRCode"
                testID="QRCode"
                timeout={10000}
              >
                <QRCode
                  value={AccountStore.getQrCode() ?? ''}
                  size={Styles.ScreenWidth / 2.2}
                />
              </BrightnessTouchable>
            </LinearGradient>
          </View>

          {scanScreenDescriptionButtons()}
        </LinearGradient>
      );
    }
  }, [displayBalances, defaultBalance]);

  return content;
};

const styles = StyleSheet.create({
  scanHeaderTextView: {
    paddingHorizontal: Styles.Spacing.m3 + 2,
    marginTop: Styles.Spacing.m4 + Styles.Spacing.m3,
    zIndex: 1,
    alignSelf: 'flex-start',
    justifyContent: 'flex-end',
  },
  scanHeaderTextDesc: {
    fontWeight: '700',
    fontSize: getLayoutDescriber().headerFontSize(),
    fontFamily: Styles.FontFamily.aeonikRegular,
    color: Styles.black,
  },
  container: {
    backgroundColor: Styles.darkBlueRevolveColor,
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
  },
  redesignContainer: {
    flex: 1,
    alignItems: 'center',
  },
  redesignQrCodeView: {
    borderWidth: 10,
    borderColor: '#FFF',
    borderRadius: 10,
  },
  gradientBoxView: {
    borderRadius: 40,
    ...getLayoutDescriber().shadowStyles().shadow,
    marginVertical: Styles.Spacing.m4,
  },
  gradientBox: {
    width: Styles.ScreenWidth / 1.6,
    height: Styles.ScreenWidth / 1.6,
    borderRadius: Styles.Spacing.m4,
    borderWidth: 1,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
    justifyContent: 'center',
    alignItems: 'center',
  },

  content: {
    marginTop: Styles.Spacing.m3,
    marginHorizontal: Styles.Spacing.m3 + Styles.Spacing.m2,
    width: Styles.ScreenWidth - (Styles.Spacing.m3 + Styles.Spacing.m2) * 2,
    backgroundColor: Styles.white,
    height: '88%',
    alignItems: 'center',
    borderRadius: Styles.Spacing.m3,
    ...getLayoutDescriber().shadowStyles().shadow,
  },
  logoView: {
    height: 65,
    width: '100%',
    marginTop: Styles.Spacing.m0,
    backgroundColor: Styles.primaryColor,
    borderTopLeftRadius: Styles.Spacing.m2,
    borderTopRightRadius: Styles.Spacing.m2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logoImage: {
    width: 98,
    height: 98,
    marginBottom: -138,
  },
  qrCodeView: {
    aspectRatio: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderWidth: Styles.Spacing.m2 + 2,
    borderColor: Styles.Colors.PayNew.neutralHuesBase09,
    borderRadius: Styles.Spacing.m2,
    overflow: 'hidden',
  },
  balanceContainer: {
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m1,
    alignItems: 'center',
  },
  balanceView: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'row',
  },
  ponitsText: {
    marginTop: Styles.Spacing.m2,
    color: Styles.darkColor,
    fontWeight: '400',
    fontSize: Styles.Fonts.f0,
    fontFamily: Styles.FontFamily.figtreeRegular,
  },
  textButtonContainer: {
    minHeight: Styles.Heights.touchTargetHeight1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  buttonText: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '700',
    color: Styles.blueRevolve,
    textDecorationLine: 'underline',
    fontFamily: Styles.FontFamily.robotoRegular,
  },
  bottomButtonsView: {
    justifyContent: 'flex-start',
    alignItems: 'center',
    marginTop: Styles.Spacing.m3,
  },
  overlay: {
    width: Styles.ScreenWidth,
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
  },
  descMain: {
    top: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btnMainView: {
    top: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  refiveBackground: {
    opacity: 0.95,
    justifyContent: 'center',
    alignItems: 'center',
    width: Styles.ScreenWidth / 1.6,
    height: Styles.ScreenWidth / 1.6,
    marginBottom: Styles.Spacing.m4,
  },
  qrView: {
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m1,
    alignItems: 'center',
  },
  aramarkContainer: {
    backgroundColor: Styles.Colors.Aramark.blue,
  },
  aramarkQRCodeView: {
    backgroundColor: Styles.Colors.Aramark.blueBase02,
    borderColor: Styles.Colors.PayNew.neutralHuesBase10,
  },
});

export default withForwardedNavigationParams()(
  withIsConnected(ScanAccountScreen),
);
